/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  ProcessCardSkeleton as CardSkeleton,
  Snackbar
} from '@plataforma/components';
import {
  USER_ACCESS_COOKIE,
  USER_TYPE,
  USER_CHAT,
  PRIVACY_POLICY,
  PLATFORM_RESPONSIBILITY_TERM,
  BONUS_JOURNEY_TERMS_COOKIE,
  TIME_TO_SHOW_MODAL_BONUS_JOURNEY_TERM_IN_DAYS,
  ERROR_MESSAGES,
  NEWS_MODAL_COOKIE,
  TIME_TO_SHOW_MODAL_NEWS_IN_DAYS,
  getBrokerFederalId,
  getLastCampaingBonusJourney
} from '@plataforma/utils';
import { useTranslation } from 'react-i18next';
import useModalBrokerUsers from '_/hooks/useModalBrokerUsers';
import {
  PoliciesAPI,
  TermsAPI,
  AuthAPI,
  NpsAPI,
  FirebaseNpsDeniedEmissionAPI,
  NovoCotadorAPI,
  BonusJournetAPI
} from '@services/api';
import {
  Authentication,
  logout,
  getSuperUser,
  isSuperUserLogged,
  getUserProfile,
  getAuth
} from '@services/auth';
import { useRemoteConfig, types } from '_/context/RemoteConfigContext';
import { zenDesk, GTMServices } from '@services/gtm';
import Cookies from 'js-cookie';
import { NPSWidget } from 'junto-ui';
import { subDays, formatISO, addDays, differenceInDays } from 'date-fns';

import { getSessionItem } from '@services/api/src/Domains/Enviroments/EnvironmentsService';
import UserSelectModal from '../../components/UserSelectModal';
import ProcessesWrapper from '../../components/ProcessesWrapper';
import { Terms } from '../../components/Terms';
import CardList from '../CardList';
import useCookie from '../../../../hooks/useCookie';
import useQuery from '../../../../hooks/useQuery';
import {
  fetchProcesses,
  fetchPolicyHolders,
  resetPolicyHolders,
  updateBroker,
  fetchUser,
  loadingProcesses
} from '../../actions';
import { BonusJourneyTerms } from '../../components/BonusJourneyTerms';
import { News } from '../../components/News';

function ProcessList() {
  const [search, setSearch] = useState({ term: '', args: {} });
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState({
    open: false,
    type: 'registerValidation.success'
  });
  const [modalStatus, setModalStatus] = useState({
    open: false,
    type: 'unauthorized'
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [remoteState, dispatchContext] = useRemoteConfig();
  const [termsToAccept, setTermsToAccept] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [hasBonusAvaliable, setHasBonusAvaliable] = useState(false);
  const [userCookie, setUserCookie] = useCookie(USER_CHAT);
  const [showNPS, setShowNPS] = useState(false);
  const [nps, setNPS] = useState({});
  const [defaultFilter, setDefaultFilter] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState({});
  const {
    brokerUsersModalState,
    handleOnConfirmUser,
    handleOnChangeUserSelect,
    userChangeMenu,
    identifyUser,
    selectedUser
  } = useModalBrokerUsers();
  const queryParams = useQuery();
  const federalId = queryParams.get('federalId');
  const policyHolderName = queryParams.get('name');
  const TOAST_TYPE = {
    'registerValidation.success': {
      variant: 'success',
      message: t('toast.registerValidation.success')
    },
    'registerValidation.error': {
      variant: 'error',
      message: t('toast.registerValidation.error')
    },
    'statementResponsibility.error': {
      variant: 'error',
      message: t('modal.statementResponsibility.error')
    },
    bonusJourney: {
      variant: 'error',
      message: ERROR_MESSAGES[400]
    }
  };

  const selectStatusOptions = [
    { value: 0, label: 'Todos' },
    { value: 2, label: 'Vigentes' },
    { value: 5, label: 'À vencer' },
    { value: 6, label: 'Vencidos' },
    { value: 3, label: 'Cancelados' },
    { value: 1, label: 'Em aberto' },
    { value: 4, label: 'Em análise' }
  ];

  const { processes, login, policyHolders, user } = useSelector(
    state => ({
      processes: state.processes,
      login: state.login,
      policyHolders: state.policyHolders,
      user: state.user
    }),
    shallowEqual
  );
  useEffect(() => {
    const userID =
      login.user.id ||
      JSON.parse(window.localStorage.getItem('broker')).user.id;

    if (userID) {
      dispatch(fetchUser(userID));
    }
  }, [dispatch, login.user.id]); // eslint-disable-line

  useEffect(() => {
    const getPolicyHolder = async () => {
      if (federalId) {
        dispatch(loadingProcesses(true));
        const broker = JSON.parse(localStorage.getItem('broker'));

        await NovoCotadorAPI.FixedpolicyHolderSearch(
          broker.externalId,
          federalId
        ).then(response => {
          if (response.data) {
            const dataFormat = {
              value: federalId,
              label: response.data.CompanyName,
              id: response.data.ExternalId
            };

            setSearch({ term: dataFormat, args: {} });
            setSelectedStatus(selectStatusOptions[1]);
            handleSearch({
              policyHolderid: response.data.ExternalId,
              status: 2
            });

            setDefaultFilter(true);
          }
        });
      } else if (login.user.userTypeDescription) {
        fetchProcessesList(null, login.user.userTypeDescription);
      }
    };
    getPolicyHolder();
  }, [login.user.userTypeDescription]); // eslint-disable-line

  useEffect(() => {
    const getBonusJourneyAvailable = async () => {
      try {
        const brokerFederalId = await getBrokerFederalId();
        const { campaignId } = await getLastCampaingBonusJourney();
        const response = await BonusJournetAPI.getBonusAvailable(
          campaignId,
          brokerFederalId
        );
        if (response.data.bonusAvaliable > 0) setHasBonusAvaliable(true);
      } catch (error) {
        setHasBonusAvaliable(false);
      }
    };
    const { bonusJourney } = getAuth();
    const { isEligible, isAcceptTerm } = bonusJourney;
    if (isEligible && isAcceptTerm) getBonusJourneyAvailable();
  }, []);

  const getBroker = useCallback(() => {
    const broker = JSON.parse(window.localStorage.getItem('broker'));
    dispatch(updateBroker(broker));
  }, [dispatch]);

  const fetchProcessesList = useCallback(
    (query, userType) => {
      dispatch(fetchProcesses(query, userType));
    },
    [dispatch]
  );

  const fetchPolicyHoldersList = useCallback(
    (pageNumber, offset, policyHolder) => {
      dispatch(fetchPolicyHolders(pageNumber, offset, policyHolder));
    },
    [dispatch]
  );

  const resetPolicyHoldersList = useCallback(() => {
    dispatch(resetPolicyHolders());
  }, [dispatch]);

  const handleOnChangeSearchInput = term => {
    setSearch(prev => ({ ...prev, term }));

    if (term !== '' && term.value <= 5) {
      GTMServices.dispatchGtm(
        'Lista de Processos',
        'Filter',
        `Status - ${term.label}`,
        'Clique'
      );
    }
  };

  const handleOnSelectStatus = selected => {
    setSelectedStatus(selected);
    if (selected.value === 0) {
      handleSearch({
        policyHolderid: search.term.id
      });
    } else {
      handleSearch({
        policyHolderid: search.term.id,
        status: selected.value
      });
    }
  };

  const handleOnChangePagination = useCallback(
    (e, pageNumber) => {
      window.scrollTo(0, 0);
      fetchProcessesList(
        { page: pageNumber, args: search.args },
        login.user.userTypeDescription
      );
      setPage(pageNumber);
    },
    [fetchProcessesList, login.user.userTypeDescription, search.args]
  );

  const handleSearch = useCallback(
    terms => {
      setSearch(prev => ({ ...prev, args: terms }));
      setPage(1);
      fetchProcessesList(
        { page: 1, args: terms },
        login.user.userTypeDescription
      );
      if (selectedStatus === null) {
        setSelectedStatus(selectStatusOptions[0]);
      }
    },
    [
      fetchProcessesList,
      login.user.userTypeDescription,
      selectStatusOptions,
      selectedStatus
    ]
  );

  const handlePolicyHolderSearch = useCallback(
    policyHolder => {
      setSelectedStatus(null);
      if (policyHolder.length > 3) {
        fetchPolicyHoldersList(1, null, policyHolder);
      }
    },
    [fetchPolicyHoldersList]
  );

  const handleEmptyStateAction = () => {
    window.location = `${getSessionItem(
      'PLATAFORMA_URL'
    )}quote/modalities-express`;
  };

  const handleEmptyErrorAction = () => {
    window.location.reload();
  };

  const getAcceptanceTerms = async () => {
    let { userId } = Cookies.getJSON(USER_ACCESS_COOKIE);

    if (!userId) {
      const auth = new Authentication();
      await auth.refreshToken();

      ({ userId } = Cookies.getJSON(USER_ACCESS_COOKIE).userId);
    }

    setLoadingTerms(true);
    try {
      const response = await TermsAPI.getAcceptanceTerm(userId);
      const unFiltredTerms =
        (response && response.data && response.data.data) || [];

      const localTermsToAccept = unFiltredTerms.filter(
        e =>
          e.description === PRIVACY_POLICY ||
          e.description === PLATFORM_RESPONSIBILITY_TERM
      );

      const hasTermToAccept = localTermsToAccept.length > 0;
      setModalStatus({ open: false });

      if (hasTermToAccept) {
        setTermsToAccept(localTermsToAccept);
        setModalStatus({ open: true, type: 'statementResponsibility' });
        disableNewsModal();
      }

      return hasTermToAccept;
    } catch (error) {
      setModalStatus({ open: true, type: 'statementResponsibilityError' });
      return true;
    } finally {
      setLoadingTerms(false);
    }
  };

  // eslint-disable-next-line consistent-return
  async function toggleModal(type) {
    const modalType =
      type === 'invalidRegister' &&
      login.user.userTypeDescription === USER_TYPE.policyholder
        ? 'invalidRegisterPolicyHolder'
        : type;

    if (type === 'bonusJourneyTerms') {
      const auth = new Authentication();
      auth.setHideIncentiveTermsModal(true);
    }

    if (type === 'statementResponsibilityError') {
      window.location.reload();
      return false;
    }

    if (type === 'news') {
      disableNewsModal();
    }

    setModalStatus(prevStatus => ({
      open: !prevStatus.open,
      type: modalType || prevStatus.type
    }));
  }

  async function handleBrokerRegister() {
    let isRegistered = false;
    try {
      const { data = {} } = await PoliciesAPI.getRegisterValidationSusep();
      isRegistered = data.registered;
      dispatchContext({ type: types.SUSEP, payload: isRegistered });

      toggleModal();

      if (!isRegistered) {
        setToast({ open: true, type: 'registerValidation.error' });
        return;
      }

      setToast({ open: true, type: 'registerValidation.success' });
    } catch (error) {
      toggleModal();
      setToast({ open: true, type: 'registerValidation.error' });
    }

    if (isRegistered) {
      const auth = new Authentication();
      await auth.refreshToken();
    }
  }

  function closeModalSelectUser() {
    handleOnConfirmUser();
    toggleModal('changeUser');
  }

  function setUserChat(name, email) {
    const access = Cookies.getJSON(USER_ACCESS_COOKIE);
    const emailLocal = email || access.email;
    const userChat = { name, email: emailLocal };

    setUserCookie(userChat);
  }

  const rederizeModalOnStart = useCallback(async () => {
    const stopedByAcceptanceTerm = await getAcceptanceTerms();
    const stopedByIdentifyUser = (await identifyUser()) && !isSuperUserLogged();

    if (stopedByAcceptanceTerm) {
      return false;
    }

    if (stopedByIdentifyUser) {
      setModalStatus({ open: true, type: 'changeUser' });
      return false;
    }

    return true;
  }, [getAcceptanceTerms, identifyUser]);

  const getRestrictionAccessByGV = useCallback(async () => {
    const isRestrictedByGv = await AuthAPI.getRestrictionAccessByGV();

    if (isRestrictedByGv) {
      logout();
    }
  }, []);

  function toggleVisibilityChat() {
    const styleChat = document.querySelector('#styleChat');

    if (modalStatus.open && modalStatus.type === 'changeUser') {
      const styleContent = '#launcher { top:-99999px; opacity: 0; }';
      const styleIframeChat = document.createElement('style');
      styleIframeChat.setAttribute('id', 'styleChat');
      styleIframeChat.innerHTML = styleContent;

      document.querySelectorAll('body')[0].append(styleIframeChat);
    } else if (styleChat) {
      styleChat.remove();
    }
  }

  function disableNewsModal() {
    Cookies.set(NEWS_MODAL_COOKIE, true, {
      expires: addDays(new Date(), TIME_TO_SHOW_MODAL_NEWS_IN_DAYS),
      domain: `${process.env.REACT_APP_DOMAIN}`
    });
  }

  function showNewsModal() {
    const hideModal = Cookies.get(NEWS_MODAL_COOKIE);
    const userProfile = getUserProfile();

    if (
      loadingTerms ||
      modalStatus.open ||
      hideModal ||
      !remoteState.config.showNewsModal ||
      userProfile === USER_TYPE.policyholder
    ) {
      return;
    }
    setModalStatus({ open: true, type: 'news' });
  }

  useEffect(() => {
    toggleVisibilityChat();
  }, [modalStatus]); // eslint-disable-line

  useEffect(() => {
    getBroker();
  }, [getBroker]);

  useEffect(() => {
    getRestrictionAccessByGV();
    rederizeModalOnStart();

    if (isSuperUserLogged()) {
      const superUser = getSuperUser();
      setUserChat(superUser.username, superUser.email);
    }
  }, []); //eslint-disable-line

  useEffect(() => {
    const selectedUserLocal = Cookies.getJSON(USER_CHAT);
    if (selectedUser && selectedUserLocal) {
      if (selectedUser.name !== selectedUserLocal.name) {
        setUserChat(selectedUser.name);
        zenDesk.init();
      }
    }
  }, [selectedUser]); //eslint-disable-line

  useEffect(() => {
    const { bonusJourney } = getAuth();
    const {
      isEligible,
      isAcceptTerm,
      lastCampaign,
      hideIncentiveTermsModal
    } = bonusJourney;
    const userProfile = getUserProfile();
    const daysLeftForAcceptance = differenceInDays(
      new Date(lastCampaign.dateLimitAccept),
      new Date()
    );

    if (hideIncentiveTermsModal || loadingTerms || modalStatus.open) {
      return;
    }

    if (
      isEligible &&
      !isAcceptTerm &&
      daysLeftForAcceptance > 0 &&
      userProfile === USER_TYPE.broker &&
      remoteState.config.bonusJourneyConfig.showModalAcceptTerms
    ) {
      setModalStatus({ open: true, type: 'bonusJourneyTerms' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingTerms,
    remoteState.config.bonusJourneyConfig.showModalAcceptTerms
  ]);

  useEffect(() => {
    showNewsModal();
  }, [
    loadingTerms,
    modalStatus,
    remoteState.config.showNewsModal,
    showNewsModal
  ]);

  const countPagination = Math.ceil(
    processes.list.numberOfRecords / processes.list.pageSize
  );

  const MODAL_TYPE = {
    unauthorized: {
      title: t('modal.blockReopenProcess.title'),
      body: t('modal.blockReopenProcess.text'),
      icon: 'Warning',
      buttons: [
        {
          text: t('modal.blockReopenProcess.button'),
          onClick: () => toggleModal()
        }
      ]
    },
    invalidRegister: {
      title: t('modal.brokerUpdateSusepRegistration.title'),
      body: t('modal.brokerUpdateSusepRegistration.text'),
      footnote: t('modal.brokerUpdateSusepRegistration.footnote'),
      icon: 'Warning',
      buttons: [
        {
          text: t('modal.brokerUpdateSusepRegistration.validateButton'),
          onClick: handleBrokerRegister
        },
        {
          text: t('modal.brokerUpdateSusepRegistration.cancelButton'),
          onClick: () => toggleModal(),
          variant: 'outlined'
        }
      ]
    },
    invalidRegisterPolicyHolder: {
      title: t('modal.policyHolderUpdateSusepRegistration.title'),
      body: t('modal.policyHolderUpdateSusepRegistration.text'),
      icon: 'Warning',
      buttons: [
        {
          text: t('modal.policyHolderUpdateSusepRegistration.button'),
          onClick: () => toggleModal()
        }
      ]
    },
    statementResponsibility: {
      body: (
        <Terms
          toggleModal={toggleModal}
          fetchedTerms={termsToAccept}
          setModalStatus={setModalStatus}
        />
      ),
      hideCloseButton: true,
      closeModal: () => {},
      noIcon: true,
      hasHTML: true
    },
    changeUser: {
      title: t('processList.userSelectModal.title'),
      body: (
        <UserSelectModal
          users={brokerUsersModalState.list}
          onSelectUser={handleOnChangeUserSelect}
          userSelected={brokerUsersModalState.selectedUser}
        />
      ),
      icon: 'Logo',
      buttons: [
        {
          text: t('processList.userSelectModal.buttonLabel'),
          onClick: closeModalSelectUser,
          testid: 'btn-select-user'
        }
      ],
      hasHTML: true,
      closeModal: () => {},
      modalSelectUser: true,
      hideCloseButton: true
    },
    statementResponsibilityError: {
      title: t('modal.statementResponsibility.error.title'),
      body: t('modal.statementResponsibility.error.text'),
      icon: 'Warning',
      buttons: [
        {
          text: t('modal.statementResponsibility.error.btnText'),
          onClick: () => toggleModal('statementResponsibilityError'),
          testid: 'btn-accept-term-error'
        }
      ],
      hideCloseButton: true,
      closeModal: () => {}
    },
    bonusJourneyTerms: {
      body: <BonusJourneyTerms toggleModal={toggleModal} setToast={setToast} />,
      darkTheme: true,
      hideCloseButton: false,
      closeModal: () => toggleModal('bonusJourneyTerms'),
      noIcon: true,
      hasHTML: true
    },
    news: {
      body: <News />,
      hideCloseButton: false,
      closeModal: () => toggleModal('news'),
      noIcon: true,
      hasHTML: true
    }
  };

  useEffect(() => {
    FirebaseNpsDeniedEmissionAPI.getNotifications().then(r => {
      if (
        r &&
        r.value &&
        getUserProfile() === USER_TYPE.broker &&
        formatISO(new Date(r.DateDenied)) >= formatISO(subDays(new Date(), 5))
      ) {
        handleNPSwidget();
      }
    });
  }, []); //eslint-disable-line

  function handleSubmit({ rating, reason }) {
    const access = Cookies.getJSON(USER_ACCESS_COOKIE);
    const formData = {
      actionId: getSessionItem('PLATAFORMA_NPS_DENIED_EMISSION_ACTION_ID'),
      email: access.email,
      name: access.broker.name,
      score: rating,
      feedback: reason,
      inviteId: nps.inviteId
    };
    NpsAPI.postAnswersAction(formData);
  }
  async function handleNPSwidget() {
    const access = Cookies.getJSON(USER_ACCESS_COOKIE);
    const { data } = await NpsAPI.getAction(
      access.email,
      access.broker.name,
      getSessionItem('PLATAFORMA_NPS_DENIED_EMISSION_ACTION_ID')
    );
    setNPS(data);
    setShowNPS(data.shouldAnswer);
  }
  return (
    <>
      <ProcessesWrapper
        search={{
          term: search.term,
          onChangeSearchInput: handleOnChangeSearchInput,
          onSearch: handleSearch,
          onSelectInputChange: handlePolicyHolderSearch,
          isFetchingPolicyHolders: policyHolders.isFetchingPolicyHolders,
          policyHoldersList: policyHolders.policyHoldersList,
          resetPolicyHoldersList
        }}
        showEmptyState={
          !processes.isFetching &&
          !processes.list.records.length &&
          !search.term &&
          processes.errors.length === 0
        }
        showEmptySearch={
          !processes.isFetching &&
          !processes.list.records.length &&
          !!search.term &&
          processes.errors.length === 0
        }
        showErrorState={processes.errors?.length > 0}
        emptyStateAction={handleEmptyStateAction}
        errorStateAction={handleEmptyErrorAction}
        pagination={{
          show: !!processes.list.records.length,
          pageNumber: page,
          count: countPagination > 1 ? countPagination : 0,
          onChange: handleOnChangePagination,
          disabled: processes.isFetching
        }}
        callbackMenu={toggleModal}
        announcement={remoteState.config.announcement}
        banners={remoteState.config.banners}
        hasBonusAvaliable={hasBonusAvaliable}
        modalStatus={modalStatus}
        modalType={MODAL_TYPE}
        setFilter={federalId && federalId ? 2 : 1}
        selectStatusOptions={selectStatusOptions}
        selectedStatus={selectedStatus}
        setSelectedStatus={handleOnSelectStatus}
      >
        <>
          {processes.isFetching ? (
            Array.from({ length: 4 }).map(() => (
              <CardSkeleton key={Math.random()} />
            ))
          ) : (
            <>
              <CardList
                items={processes.list.records}
                resumeAction={toggleModal}
                user={user}
              />
            </>
          )}

          <Snackbar
            open={toast.open}
            message={TOAST_TYPE[toast.type].message}
            variant={TOAST_TYPE[toast.type].variant}
            onClose={() => setToast(prev => ({ ...prev, open: false }))}
          />
        </>
        <NPSWidget
          onClose={() => setShowNPS(false)}
          onSubmit={handleSubmit}
          isOpen={showNPS}
          title={nps.question}
        />
      </ProcessesWrapper>
    </>
  );
}

export default ProcessList;
